import React, { useState, useContext } from 'react';
import { CartContext } from '../context/CartContext';
import ProductDetails from './ProductDetails';
import { formatAmount, hideIfNA } from '../utils/formatUtils';
import './ProductCard.css'; // Updated CSS import

const ProductCard = ({ product, vendorName, logoUrl, vendorId, orderType }) => {
  const { addToCart } = useContext(CartContext);
  const [showDetails, setShowDetails] = useState(false);

  const handleDetailsOpen = () => {
    // Add debug log to check product before opening modal
    console.log('Product being passed to ProductDetails:', { 
      id: product._id, 
      name: product.name, 
      description: product.description 
    });
    setShowDetails(true);
  };
  
  const handleDetailsClose = () => setShowDetails(false);

  // Early return after hooks are called
  if (!product.image || product.image === '/placeholder-image.png') {
    return null;
  }
  
  // Safeguard values
  const price = product.price ?? 0;
  const salePrice = product.salePrice ?? null;
  const strain = hideIfNA(product.strain);
  const image = product.image || '/placeholder-image.png';
  const amount = formatAmount(product.amount);
  const tags = product.tags || [];
  const isStaffPick = tags.includes('Staff Pick');
  const otherTag = !isStaffPick ? tags.find(tag => ['High THC', 'Low THC'].includes(tag)) : null;
  // Ensure description is captured
  const description = product.description || '';
  

  return (
    <>
      <div className="pc-card">
        {/* SALE Banner */}
        {salePrice && (
          <div className="pc-sale-banner">
            SALE
          </div>
        )}

        {/* Product Image and Badges Container */}
        <div className="pc-image-container">
          {/* Staff Pick or Other Tag Badge */}
          {(isStaffPick || otherTag) && (
            <div className={`pc-tag-badge ${isStaffPick ? 'pc-staff-pick' : 'pc-other-tag'}`}>
              {isStaffPick ? 'Staff Pick' : otherTag}
            </div>
          )}
          
          <img
            src={image}
            alt={product.name || 'Product Image'}
            className="pc-product-image"
            onClick={handleDetailsOpen}
          />
          
          {/* Amount Badge - Only show if amount exists */}
          {amount && (
            <span className="pc-amount-badge">
              {amount}
            </span>
          )}
        </div>

        {/* Card Body */}
        <div className="pc-card-body">
          {/* Product Brand */}
          <div className="pc-brand-container">
            <p className="pc-brand-text">
              {product.brand || 'No Brand'}
            </p>
          </div>

          {/* Product Name and Strain */}
          <div className="pc-product-info">
            <p className="pc-product-name" onClick={handleDetailsOpen}>
              {product.name}
            </p>
            {strain && (
              <p className="pc-strain-text" onClick={handleDetailsOpen}>
                {strain}
              </p>
            )}
          </div>

          {/* Footer Section */}
          <div className="pc-footer">
            {/* Price Section */}
            <div className="pc-price-container">
              {salePrice ? (
                <>
                  <span className="pc-sale-price">
                    ${salePrice.toFixed(2)}
                    <span className="pc-original-price">
                      ${price.toFixed(2)}
                    </span>
                  </span>
                </>
              ) : (
                <span className="pc-regular-price">
                  ${price.toFixed(2)}
                </span>
              )}
            </div>

            {/* Add to Cart Button */}
            <div className="pc-button-container">
              <button
                className="pc-add-button"
                onClick={(e) => {
                  e.stopPropagation();
                  addToCart({
                    ...product,
                    price: price,
                    salePrice: salePrice,
                    vendorName,
                    logoUrl,
                    primaryImage: image,
                    strain: strain,
                    vendorId,
                    productId: product._id,
                    orderType,
                  });
                }}
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Product Details Modal */}
      <ProductDetails
        show={showDetails}
        handleClose={handleDetailsClose}
        product={{
          ...product,
          description: description,
          strainDetails: product.strainDetails || product.variation?.strainDetails || ''
        }}
        vendorName={vendorName}
        logoUrl={logoUrl}
        vendorId={vendorId}
      />
    </>
  );
};

export default ProductCard;